import { useTranslation } from 'react-i18next';
import { notify } from '../../components/ui/toast';
import { NEXT_STATUS } from '../../components/ui/status';
import { useApiError } from '../../hooks/useApiError';
import { getApiError } from '../../services/api';
import type { Order, OrderStatus } from '../../types/Order';
import type { PaymentChange } from './orderApi';
import type { OrderOps } from './useOrdersFeed';

/** The status "Move back" returns to. Cancelled orders are reopened to New. */
export const PREVIOUS_STATUS: Partial<Record<OrderStatus, OrderStatus>> = {
    accepted: 'pending',
    preparing: 'accepted',
    ready: 'preparing',
    completed: 'ready',
    cancelled: 'pending',
};

/** User-facing order actions: every move shows an undo toast, every failure an error toast with Retry. */
export function useOrderActions(ops: OrderOps) {
    const { t } = useTranslation(['orders', 'common']);
    const apiError = useApiError();

    // A closed billing month never accepts the change, so it gets no Retry.
    const fail = (e: unknown, retry?: () => void) =>
        notify.error(apiError(e), retry && getApiError(e).code !== 'MONTH_CLOSED' ? { retry: { label: t('common:ui.retry'), run: retry } } : undefined);

    const move = async (order: Order, to: OrderStatus, message: string, send: () => Promise<void>): Promise<void> => {
        const undo = async () => {
            try {
                await ops.revert(order.id, order.status);
            } catch (e) {
                fail(e);
            }
        };
        try {
            await send();
            notify.success(message, { undo: { label: t('toast.undo'), run: () => void undo() } });
        } catch (e) {
            fail(e, () => void move(order, to, message, send));
        }
    };

    const moveTo = (order: Order, to: OrderStatus) =>
        move(order, to, t('toast.moved', { number: order.orderNumber, status: t(`common:status.${to}`) }), () => ops.revert(order.id, to));

    return {
        advance: (order: Order) => {
            const to = NEXT_STATUS[order.status];
            return to
                ? move(order, to, t('toast.moved', { number: order.orderNumber, status: t(`common:status.${to}`) }), () => ops.advance(order.id))
                : Promise.resolve();
        },
        cancel: (order: Order) =>
            move(order, 'cancelled', t('toast.cancelled', { number: order.orderNumber }), () => ops.cancel(order.id)),
        moveTo,
        back: (order: Order) => {
            const to = PREVIOUS_STATUS[order.status];
            return to ? moveTo(order, to) : Promise.resolve();
        },
        pay: async function pay(order: Order, p: PaymentChange): Promise<void> {
            try {
                await ops.setPayment(order.id, p);
                notify.success(
                    p.status === 'paid'
                        ? p.method
                            ? t('toast.paidWith', { number: order.orderNumber, method: t(`common:payment.${p.method}`) })
                            : t('toast.paid', { number: order.orderNumber })
                        : t('toast.unpaid', { number: order.orderNumber })
                );
            } catch (e) {
                fail(e, () => void pay(order, p));
            }
        },
    };
}
